import React, { ReactNode } from 'react';
import './button.css';
import { DownloadTableExcel } from 'react-export-table-to-excel';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ReactToPrint from 'react-to-print';
import { fontWeight } from 'html2canvas/dist/types/css/property-descriptors/font-weight';
import { useTheme } from '../../context/ThemeContext.js';

interface ButtonProps {
	children?: ReactNode;
	type:  | 'close'
  | 'download'
  | 'save'
  | 'update'
  | 'fetch'
  | 'delete'
  | 'print'
  | 'excel'
  | 'pdf'
  | 'doc'
  | 'search'
  | 'submit'
  | 'edit'
  | 'refresh'
  | 'call'
  | 'verify'
  | 'reject'
  | 'complete'
  | 'view'
  | 'info';
	onClick?: (e: any) => any;
	loader?: boolean;
	fileName?: string;
	tableRef?: any;
	pdfId?: string;
	width?: string;
	height?: string;
	disabled?: boolean;
	hoverColor?: string;
	color?: string;
	fontSize?: string;
	value?: string;
	orientation?: any;
	pageWidth?: number;
	pageHeights?: number;
	paperSize?: any;
}

const Button: React.FC<ButtonProps> = ({ children, disabled, type,value, onClick, loader, fileName, tableRef, pdfId, height = 'auto', width = 'auto', hoverColor, color, fontSize = 'auto', orientation = 'p', pageWidth, pageHeights, paperSize = 'a4' }) => {
	const { theme } = useTheme();

 const buttonColors = {
  lightMode: {
    close: '#ef4444',
    download: '#3b82f6',
    save: '#10b981',
    update: '#f59e0b',
    fetch: '#a855f7',
    delete: '#ef4444',
    print: '#14b8a6',
    excel: '#22c55e',
    pdf: '#dc2626',
    doc: '#2563eb',
    search: '#f97316',
    submit: '#0ea5e9',
    edit: '#f97316',
    refresh: '#3b82f6',
    call: '#0ea5e9',        // updated
    verify: '#16a34a',
    reject: '#dc2626',
    complete: '#facc15',
    view: '#6366f1',
    info: '#3b82f6',
    giveTime: '#8b5cf6',    // updated
  },
  darkMode: {
    close: '#f87171',
    download: '#60a5fa',
    save: '#34d399',
    update: '#fbbf24',
    fetch: '#c084fc',
    delete: '#f87171',
    print: '#2dd4bf',
    excel: '#4ade80',
    pdf: '#fb7185',
    doc: '#93c5fd',
    search: '#fdba74',
    submit: '#38bdf8',
    edit: '#fdba74',
    refresh: '#60a5fa',
    call: '#38bdf8',         // updated
    verify: '#4ade80',
    reject: '#f87171',
    complete: '#fde047',
    view: '#818cf8',
    info: '#60a5fa',
    giveTime: '#a78bfa',     // updated
  },
};

 const buttonHoverColors = {
  lightMode: {
    close: '#dc2626',
    download: '#2563eb',
    save: '#059669',
    update: '#d97706',
    fetch: '#9333ea',
    delete: '#dc2626',
    print: '#0d9488',
    excel: '#16a34a',
    pdf: '#b91c1c',
    doc: '#1d4ed8',
    search: '#ea580c',
    submit: '#0284c7',
    edit: '#ea580c',
    refresh: '#2563eb',
    call: '#0284c7',         // updated
    verify: '#15803d',
    reject: '#b91c1c',
    complete: '#eab308',
    view: '#4f46e5',
    info: '#1d4ed8',
    giveTime: '#7c3aed',     // updated
  },
  darkMode: {
    close: '#dc2626',
    download: '#3b82f6',
    save: '#10b981',
    update: '#f59e0b',
    fetch: '#a855f7',
    delete: '#dc2626',
    print: '#14b8a6',
    excel: '#22c55e',
    pdf: '#e11d48',
    doc: '#60a5fa',
    search: '#fb923c',
    submit: '#0ea5e9',
    edit: '#fb923c',
    refresh: '#3b82f6',
    call: '#0ea5e9',         // updated
    verify: '#22c55e',
    reject: '#dc2626',
    complete: '#facc15',
    view: '#6366f1',
    info: '#3b82f6',
    giveTime: '#8b5cf6',     // updated
  },
};


	const colors = theme === 'dark' ? buttonColors.darkMode : buttonColors.lightMode;
	const hoverColors = theme === 'dark' ? buttonHoverColors.darkMode : buttonHoverColors.lightMode;

	const printDocument = (): void => {
		const input = document.getElementById(pdfId + '');

		if (!input) {
			return;
		}

		html2canvas(input, { scale: 2 }) // You can adjust the scale factor if needed
			.then((canvas) => {
				const imgData = canvas.toDataURL('image/png');
				let imgWidth; // A4 width in mm
				let pageHeight;
				if (orientation == 'l' || orientation == 'landscape') {
					imgWidth = pageHeights || 297; // A4 width in mm
					pageHeight = pageWidth || 210; // A4 height in mm
				} else {
					imgWidth = pageWidth || 210; // A4 width in mm
					pageHeight = pageHeights || 297; // A4 height in mm
				}
				const imgHeight = (canvas.height * imgWidth) / canvas.width;
				let heightLeft = imgHeight;
				const pdf = new jsPDF(orientation, 'mm', paperSize);
				let position = 0;

				// Add initial image
				pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
				heightLeft -= pageHeight;

				// Loop to add images until we run out of content
				while (heightLeft > 0) {
					position = heightLeft - imgHeight;
					pdf.addPage();
					pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
					heightLeft -= pageHeight;
				}

				pdf.save('download.pdf');
			})
			.catch((error) => {
				console.error('Could not generate PDF: ', error);
			});
	};

	const style = {
		backgroundColor: color ? color : disabled ? 'gray' : colors[type],
		color: disabled ? 'black' : 'white',
		padding: '0.7vh 0.7vw',
		fontSize,
		fontWeight: '600',
		width,
		height,
		border: 'none',
		borderRadius: '6px',
		margin: '5px',
		cursor: disabled ? 'not-allowed' : 'pointer',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		outline: 'none',
		boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
		transition: 'all 0.3s ease',
		'--first-Color': color ? color : disabled ? 'gray' : colors[type],
		'--second-color': color ? color : disabled ? 'gray' : colors[type],
	};

	return (
		<>
			{type === 'excel' ? (
				<DownloadTableExcel filename={fileName || 'file'} currentTableRef={tableRef}>
					<button
						style={style}
						className={loader ? 'loader' : ''}
						onMouseOver={({ target }: any) => {
							// target.style.backgroundColor = hoverColors[type];
							target.style.transform = 'translateY(-5px)';
						}}
						onMouseOut={({ target }: any) => {
							target.style.transform = 'translateY(0px)';
							target.style.backgroundColor = color ? color : colors[type] || '#2c3e50';
						}}>
						<div className='text-[#ffffffc9] dark:text-[#000000aa]' style={{ pointerEvents: 'none', userSelect: 'none' }}>
							{children}
						</div>
					</button>
				</DownloadTableExcel>
			) : type === 'print' ? (
				<ReactToPrint
					documentTitle={fileName}
					onAfterPrint={() => {
						if (onClick) {
							onClick('afterPrint');
						}
					}}
					trigger={() => (
						<button
							style={style}
							className={loader ? 'loader' : ''}
							onMouseOver={({ target }: any) => {
								target.style.backgroundColor = hoverColor ? hoverColor : hoverColors[type];
								target.style.transform = 'translateY(-5px)';
							}}
							onMouseOut={({ target }: any) => {
								target.style.transform = 'translateY(0px)';
								target.style.backgroundColor = color ? color : colors[type] || '#2c3e50';
							}}
							>
							<div className='text-[#ffffffc9] dark:text-[#000000aa]' style={{ pointerEvents: 'none', userSelect: 'none' }}>
								{children}
							</div>
						</button>
					)}
					content={() => tableRef}
				/>
			) : (
				<div>
					<button style={style} value={value} disabled={disabled} onClick={!disabled && type === 'pdf' ? () => printDocument() : onClick} className={loader ? 'loader' : ''} onMouseOver={({ target }: any) => !disabled && ((target.style.transform = 'translateY(-5px)'), (target.style.backgroundColor = hoverColor ? hoverColor : hoverColors[type]))} onMouseOut={({ target }: any) => !disabled && ((target.style.transform = 'translateY(0px)'), (target.style.backgroundColor = color ? color : colors[type] || '#2c3e50'))}>
						<div className='text-[#ffffffc9] dark:text-[#000000aa]' style={{ pointerEvents: 'none', userSelect: 'none' }}>
							{children}
						</div>
					</button>
				</div>
			)}
		</>
	);
};

export default Button;
